import { TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import i18next from "i18next";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import BasicInput from "../components/BasicInput";
import Button from "../components/Button";
import Label from "../components/Label";
import * as locationServices from "../services/locationsServices";
import { setSelectedLocation, setWatchLocation } from "../state/map/mapSlice";
import { utils } from "../utils";
import * as constants from "../utils/constants";
import { Location } from "../utils/types";
import { SelectAdressContainer } from "./styles";

type Props = {
	location: Location;
	setSelectLocationMode?: (any) => void;
	setSelectingLocation?: (any) => void;
	type: string;
	handleAccept: any;
	handleCancel: any;
	onChange?: (value: any) => void;
};

export default function SearchCoordinate({
	location,
	setSelectLocationMode,
	type,
	handleAccept,
	handleCancel,
	setSelectingLocation,
	onChange,
}: Props) {
	const dispatch = useDispatch();

	const [externalResults, setExternalResults] = useState<any[]>([]);
	const [results, setResults] = useState<any[]>([]);
	const [lat, setLat] = useState<string | number>(location?.coordinates?.lat);
	const [lng, setLng] = useState<string | number>(location?.coordinates?.lng);
	const [name, setName] = useState<string>(location.name);
	const [errors, setErrors] = useState<any>({});
	const [locationId, setLocationId] = useState<any>(null);

	const selectedLocation = useSelector((state: any) => state.mapReducer?.selectedLocation);

	useEffect(() => {
		if (type != constants.SELECT_LOCATION_MODE.locationSearch) {
			dispatch(setWatchLocation(true));
		}

		return () => {
			dispatch(setWatchLocation(false));
		};
	}, []);

	const reset = () => {
		dispatch(setSelectedLocation({ lat: 0, lng: 0 }));
		dispatch(setWatchLocation(false));
	};

	useEffect(() => {
		if (!selectedLocation || selectedLocation?.lat == 0 || selectedLocation?.lng == 0) return;

		if (selectedLocation.lat && parseFloat(selectedLocation.lat.toString()) !== parseFloat(lat?.toString())) {
			setLat(selectedLocation.lat.toString());
			setErrors({ ...errors, lat: "" });
		}
		if (selectedLocation.lng && parseFloat(selectedLocation.lng.toString()) !== parseFloat(lng?.toString())) {
			setLng(selectedLocation.lng.toString());
			setErrors({ ...errors, lng: "" });
		}
	}, [selectedLocation]);

	const handleOnChangeLocationInput = (valueP: number, isLat: boolean) => {
		const value = valueP.toString();
		if (isLat) {
			if (errors.lat) setErrors({ ...errors, lat: "" });
			setLat(value);
		} else {
			if (errors.lng) setErrors({ ...errors, lng: "" });
			setLng(value);
		}

		if (lat && !isLat && checkFloat(value)) {
			dispatch(
				setSelectedLocation({
					lat: selectedLocation?.lat,
					lng: parseFloat(value),
				})
			);
		} else if (lng && isLat && checkFloat(value)) {
			dispatch(
				setSelectedLocation({
					lat: parseFloat(value),
					lng: selectedLocation?.lng,
				})
			);
		}
	};

	const [searchValue, setSearchValue] = useState("");
	const searchValueRef = useRef<string | undefined>();
	searchValueRef.current = searchValue;

	const handleChangeSearch = utils.debounce((e) => {
		const searchValue = searchValueRef.current;

		if (!searchValue || searchValue === "") return;
		locationServices.getLocations(searchValue).then((result) => {
			setExternalResults([
				...result.map((location) => ({
					location_id: location.location_id,
					label: location?.name,
					title: location?.name + ` (${constants.APP_NAME})`,
					location: { lat: location.coordinates.lat, lng: location.coordinates.lng },
				})),
			]);
		});

		locationServices.getExternalLocations(searchValue).then((res) => {
			setResults([
				...res.features.map((location) => ({
					location_id: location.id,
					label: location?.place_name,
					title: location?.place_name,
					location: { lat: location.geometry.coordinates[1], lng: location.geometry.coordinates[0] },
				})),
			]);
		});
	}, constants.DEBOUNCE_TIME);

	const validate = (type: string) => {
		const errors: any = {};

		if (!lat || lat == "0") {
			errors.lat = i18next.t("latitude-required");
		}

		if (!lng || lng == "0") {
			errors.lng = i18next.t("longitude-required");
		}

		if (type == constants.SELECT_LOCATION_MODE.locationAdd && !name) {
			errors.name = i18next.t("location-name-required");
		}

		setErrors(errors);

		return Object.keys(errors).length === 0 ? {} : errors;
	};

	const handleOnSubmit = async () => {
		const errors = validate(type);
		if (Object.keys(errors).length !== 0) {
			toast.error(i18next.t("fill-required"));
			return;
		}
		handleAccept({ lat, lng, name, location_id: locationId });

		reset();
		setSelectLocationMode?.(null);
	};

	const checkFloat = (value: string) => {
		if (/^-?\d+(\.\d*)?$/.test(value)) {
			return true;
		}
	};

	const handleInputChange = (value, isLat) => {
		const regex = /^-?\d*\.?\d*$/;
		if (regex.test(value)) {
			handleOnChangeLocationInput(value, isLat);
		}
	};

	return (
		<SelectAdressContainer data-testid="search-coordinate-container">
			<div style={{ minWidth: 360 }}>
				{type === constants.SELECT_LOCATION_MODE.locationSearch && (
					<>
						<Label isTitle className="mb-2" text={i18next.t("location-search")} />
						<Autocomplete
							disablePortal
							options={[...results, ...externalResults]}
							lang="es"
							sx={{ width: 300 }}
							noOptionsText={i18next.t(searchValue ? "no-results" : "name-required")}
							onChange={(e, value) => {
								if (!value) {
									dispatch(setSelectedLocation({ lat: 0, lng: 0 }));
									return;
								}
								onChange?.(value);
								setLocationId(value.location_id);
								setName(value.label);
								dispatch(setSelectedLocation(value?.location));
							}}
							renderOption={(props, option) => (
								<li {...props} key={option.location_id}>
									{option.title}
								</li>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									inputProps={{
										...params.inputProps,
										"data-testid": "search-location-input",
									}}
									onChange={(e) => {
										setSearchValue(e.target.value);
										handleChangeSearch();
									}}
									style={{ minWidth: 350 }}
									placeholder={i18next.t("location-search-name")}
								/>
							)}
						/>
					</>
				)}
				{type === constants.SELECT_LOCATION_MODE.enterLatitudeLongitude && (
					<Label
						isTitle
						className="mb-0"
						text={i18next.t("location-enter-latitude-longitude-select-on-map")}
					/>
				)}
				{type === constants.SELECT_LOCATION_MODE.locationAdd && (
					<>
						<Label isTitle className="mb-0" text={i18next.t("location-create")} />
						<p>{i18next.t("location-create-description")}</p>
					</>
				)}

				{(type === constants.SELECT_LOCATION_MODE.enterLatitudeLongitude ||
					type === constants.SELECT_LOCATION_MODE.locationAdd) && (
					<div className="d-flex ">
						{type === constants.SELECT_LOCATION_MODE.locationAdd && (
							<BasicInput
								fieldTitleClassName="mt-sm-2"
								label={i18next.t("name")}
								value={name}
								onChange={(e) => setName(e.target.value)}
								error={errors.name}
								datatestid="search-name-input"
							/>
						)}
						<BasicInput
							fieldTitleClassName="mt-sm-2"
							className="ms-2"
							label={i18next.t("latitude")}
							value={lat}
							onChange={(e) => handleInputChange(e.target.value, true)}
							error={errors.lat}
							datatestid="search-lat-input"
						/>
						<BasicInput
							fieldTitleClassName="mt-sm-2"
							className="ms-2"
							label={i18next.t("longitude")}
							value={lng}
							onChange={(e) => handleInputChange(e.target.value, false)}
							datatestid="search-lng-input"
							error={errors.lng}
						/>
					</div>
				)}

				<div className="d-flex justify-content-end mt-3">
					<Button
						label={i18next.t("cancel")}
						onClick={() => {
							reset();
							handleCancel(null);
							setSelectingLocation?.(false);
						}}
						secondary
					/>
					<Button
						datatestid="accept-button"
						label={i18next.t("accept")}
						onClick={handleOnSubmit}
						className="ms-4"
					/>
				</div>
			</div>
		</SelectAdressContainer>
	);
}
